import {
  Checkbox,
  FieldSelect,
  FieldText,
  FieldWrapper,
  FlexBox,
  FormField,
  FormGrid,
  Icon,
  Modal,
  ModalBody,
  ModalFoot,
  ModalHead,
  TagSelectSuggestion,
  Text,
  Tooltip,
} from '@dixa/lib-shared-habla';
import { sizes } from '@dixa/lib-shared-tokens';
import { AnyAction } from '@reduxjs/toolkit';
import classNames from 'classnames';
import { useHasAccess } from 'lib-shared-access-control';
import { config } from 'lib-shared-backend-config';
import { ampli } from 'lib-shared-tracking';
import { Company, User } from 'lib-shared-types-api';
import { Role } from 'lib-shared-user-role';
import noop from 'lodash/noop';
import { useEffect, useState } from 'react';
import { useMap } from 'react-use';
import {
  Field,
  FieldArray,
  getFormValues,
  reduxForm,
  SubmissionError,
} from 'redux-form';
import defaultShouldAsyncValidate from 'redux-form/lib/defaultShouldAsyncValidate';

import AvatarControl from 'components/shared/avatarControl/components/AvatarControl';
import { FieldRepeater } from 'fable/fieldRepeater';
import { useAppDispatch } from 'utils/redux/useAppDispatch';
import { useAppSelector } from 'utils/redux/useAppSelector';
import { useCurrentUser } from 'utils/user';

import { ContactPointField } from './components/contactPointField/contactPointField';
import { UserModalFieldAlert } from './components/fieldAlert/userModalFieldAlert';
import { RolesField } from './components/rolesField/rolesField';
import { UserTypeField } from './components/userTypeField/userTypeField';
import { userModalActions, userModalFormName } from './userModal.actions';
import styles from './userModal.module.scss';
import { UserModalState } from './userModal.reducer';
import {
  selectUserModal,
  selectUserModalInitialValues,
} from './userModal.selectors';
import type {
  FormFields,
  UserModalFormData,
  UserModalFormProps,
} from './userModal.types';
import {
  asyncValidate,
  asyncValidationFieldNameMap,
  buildNewUser,
  buildUpdateUser,
  getAvatarUser,
  hasConflictingId,
  scimTooltipContent,
  userTypeTooltipContent,
  validate,
} from './userModal.utils';

export function UserModalImpl({
  change,
  handleSubmit,
  asyncValidating,
  destroy,
  reset,
  pristine,
  initialize,
  submitting,
}: UserModalFormProps): JSX.Element {
  const dispatch = useAppDispatch();
  const { user: profile } = useCurrentUser();
  const hasManageRoleAccess = useHasAccess('organization.access');
  const hasContactPointAccess = useHasAccess('contact.contact_point');

  const [isPricingPlanAccepted, setIsPricingPlanAccepted] = useState(false);

  const _initialValues = useAppSelector<UserModalFormData>(
    selectUserModalInitialValues,
  );
  const [initialValues, { set: patchInitialValues, setAll: setInitialValues }] =
    useMap<UserModalFormData>();
  const formValues = useAppSelector<UserModalFormData | undefined>(
    getFormValues(userModalFormName),
  );

  useEffect(() => {
    setInitialValues(_initialValues);
  }, [_initialValues, setInitialValues]);

  const { primary_email_idx = 0, primary_phone_idx = 0 } = formValues ?? {};
  const setPrimaryEmailIndex = (idx: number) =>
    change('primary_email_idx', idx);
  const setPrimaryPhoneNumberIndex = (idx: number) =>
    change('primary_phone_idx', idx);

  const { companies, meta, user, newCompany, ui, userId } =
    useAppSelector<UserModalState>(selectUserModal);

  const isCreating = userId === null;
  const isEditing = !isCreating;
  const isBotUser = formValues?.user_type === 'bot';
  const isContactUser = formValues?.user_type === 'contact';
  const isMemberUser = formValues?.user_type === 'member';
  const isSelf = userId === profile.id;
  const isSCIMEnabled = !!user?.has_scim_enabled;

  const fieldRules: Record<
    FormFields | 'pricing_plan',
    Record<'visible' | 'required' | 'disabled', boolean>
  > = {
    avatar_url: {
      visible: true,
      required: false,
      disabled: false,
    },
    given_name: {
      visible: true,
      required: isMemberUser,
      disabled: isSCIMEnabled,
    },
    family_name: {
      visible: true,
      required: isMemberUser,
      disabled: isSCIMEnabled,
    },
    name: {
      visible: true,
      required: true,
      disabled: false,
    },
    emails: {
      visible: hasContactPointAccess && !isBotUser,
      get required() {
        if (!this.visible) return false;

        return (
          isMemberUser ||
          !formValues?.phone_numbers?.some(({ value }) => value?.trim())
        );
      },
      get disabled() {
        if (!this.visible) return false;

        return (
          user?.type === 'member' &&
          initialValues.emails?.[0].value?.trim() !== ''
        );
      },
    },
    phone_numbers: {
      visible: hasContactPointAccess && !isBotUser,
      get required() {
        if (!this.visible) return false;

        return (
          isContactUser &&
          !formValues?.emails?.some(({ value }) => value?.trim())
        );
      },
      get disabled() {
        return this.visible && isSCIMEnabled;
      },
    },
    user_type: {
      visible:
        hasManageRoleAccess &&
        (isCreating ? !user?.type : !isSelf && !isBotUser),
      required: true,
      disabled: false,
    },
    custom_roles: {
      visible: hasManageRoleAccess && !isSelf && isMemberUser,
      required: true,
      disabled: false,
    },
    company_id: {
      visible: !isBotUser,
      required: false,
      disabled: false,
    },
    pricing_plan: {
      visible: isCreating && isMemberUser,
      get required() {
        return this.visible ? !isPricingPlanAccepted : false;
      },
      disabled: false,
    },
  };

  // Initialize the form with the user's data.
  useEffect(() => {
    if (!ui.isUserModalShown) return;
    dispatch(initialize(initialValues) as unknown as AnyAction);
  }, [dispatch, initialValues, initialize, ui.isUserModalShown]);

  // Fetches companies when the modal is opened.
  useEffect(() => {
    if (!ui.isUserModalShown) return;
    dispatch(userModalActions.companies.get());
  }, [dispatch, ui.isUserModalShown]);

  // Updates the form with the new company data after creating a company.
  useEffect(() => {
    if (newCompany) {
      change('company_id', newCompany.id);
      change('company', newCompany.name);
    }
  }, [newCompany, change]);

  /** Checks the payload for errors and throws redux-form errors if needed */
  const checkPayloadForErrors = (payload: unknown) => {
    if (hasConflictingId(payload)) {
      throw new SubmissionError(
        payload.errors.reduce(
          (errors, error) => ({
            ...errors,
            [error]: (
              <UserModalFieldAlert
                key={error}
                field={asyncValidationFieldNameMap[error]}
                userId={payload.conflicting_id}
              />
            ),
          }),
          {},
        ),
      );
    }
  };

  const saveUser = async (values: UserModalFormData): Promise<void> => {
    if (isCreating) {
      const { payload } = await dispatch(
        userModalActions.user.add(buildNewUser({ values })),
      );

      checkPayloadForErrors(payload);
    } else if (user) {
      const { payload } = await dispatch(
        userModalActions.user.update(buildUpdateUser({ values, user })),
      );

      checkPayloadForErrors(payload);
    }

    sendTrackingEvents(values);
  };

  const sendTrackingEvents = (values: UserModalFormData) => {
    const resolveCustomRoleTrackingValue = (role?: Role) => {
      if (!role) return;
      return role.is_system_role ? role.name : 'Custom Role';
    };

    ampli.userModalFormSubmitted({
      mode: isCreating ? 'add' : 'edit',
      ...(values.user_type === 'member' && {
        role: resolveCustomRoleTrackingValue(values.custom_roles?.[0]),
      }),
      userType: values.user_type,
      company: {
        name: companies.find((company) => company.id === values.company_id)
          ?.name,
        id: values.company_id ?? undefined,
      },
      numberOfEmails: (values.emails ?? []).length,
      numberOfPhoneNumbers: (values.phone_numbers ?? []).length,
    });

    if (isEditing) {
      if (values.user_type !== initialValues.user_type) {
        ampli.userModalUserTypeChanged({
          before: initialValues.user_type,
          after: values.user_type,
        });
      }

      const beforeRole = initialValues.custom_roles?.[0];
      const afterRole = values.custom_roles?.[0];

      if (beforeRole?.id !== afterRole?.id) {
        ampli.userModalRoleChanged({
          before: resolveCustomRoleTrackingValue(beforeRole),
          after: resolveCustomRoleTrackingValue(afterRole),
        });
      }
    }
  };

  const addCompany = (name: string): void => {
    dispatch(
      userModalActions.companies.add({
        name,
        user_ids: [],
      }),
    );
  };

  const closeModal = (): void => {
    reset();
    destroy();
    setIsPricingPlanAccepted(false);
    dispatch(userModalActions.hide());
  };

  const handleSelectAvatar = (dataURI: string): void => {
    change('avatar_url', dataURI);
  };

  const handleDeleteAvatar = (): void => {
    change('avatar_url', null);
  };

  return (
    <Modal
      isOpen={ui.isUserModalShown}
      onClose={closeModal}
      className={styles.modal}
    >
      <div className="user-modal" id="user-modal">
        <form onSubmit={handleSubmit(saveUser)} noValidate>
          <ModalHead
            title={
              userId === null
                ? 'Add a new user'
                : `Edit ${initialValues?.name ?? ''}`
            }
            onClose={closeModal}
          />

          <ModalBody>
            <FormGrid>
              {fieldRules.avatar_url.visible && (
                <FormField>
                  <AvatarControl
                    user={getAvatarUser(user, formValues as User)}
                    onSelectAvatar={handleSelectAvatar}
                    onDeleteAvatar={handleDeleteAvatar}
                  />
                </FormField>
              )}

              {fieldRules.given_name.visible && (
                <FormField span={3}>
                  <Tooltip
                    content={scimTooltipContent}
                    disabled={!isSCIMEnabled}
                  >
                    <Field
                      label="First name"
                      name="given_name"
                      placeholder="Enter contact’s first name"
                      component={FieldText}
                      disabled={fieldRules.given_name.disabled}
                      required={fieldRules.given_name.required}
                    />
                  </Tooltip>
                </FormField>
              )}

              {fieldRules.family_name.visible && (
                <FormField span={3}>
                  <Tooltip
                    content={scimTooltipContent}
                    disabled={!isSCIMEnabled}
                  >
                    <Field
                      label="Last name"
                      name="family_name"
                      placeholder="Enter contact’s last name"
                      component={FieldText}
                      disabled={fieldRules.family_name.disabled}
                      required={fieldRules.family_name.required}
                    />
                  </Tooltip>
                </FormField>
              )}

              {fieldRules.name.visible && (
                <FormField>
                  <Field
                    label="Display name"
                    name="name"
                    placeholder="Display name will be visible to your customers"
                    component={FieldText}
                    disabled={fieldRules.name.disabled}
                    required={fieldRules.name.required}
                    autoFocus={isCreating}
                  />
                </FormField>
              )}

              {fieldRules.emails.visible && (
                <FormField>
                  <FieldWrapper
                    label="Emails"
                    required={fieldRules.emails.required}
                  >
                    <FieldArray
                      buttonProps={{
                        placement: 'outside',
                        label: 'Add email',
                        icon: 'plus',
                        className: classNames(styles.fieldRepeaterButton),
                      }}
                      className={styles.fieldRepeater}
                      style={{ borderTop: 'none' }}
                      component={FieldRepeater}
                      rowProps={{
                        type: 'email',
                        selectedIndex: primary_email_idx,
                        onSelect: setPrimaryEmailIndex,
                        disabledPrimary: fieldRules.emails.disabled,
                      }}
                      withBackground={false}
                      rowComponent={ContactPointField}
                      name="emails"
                    />
                  </FieldWrapper>
                </FormField>
              )}

              {fieldRules.phone_numbers.visible && (
                <FormField>
                  <Tooltip
                    content={scimTooltipContent}
                    disabled={!isSCIMEnabled}
                  >
                    <FieldWrapper
                      label="Phone numbers"
                      helpIcon={{
                        text: 'Phone numbers should start with "+" eg. +1 888 910 7555.',
                      }}
                      required={fieldRules.phone_numbers.required}
                      disabled={fieldRules.phone_numbers.disabled}
                    >
                      <FieldArray
                        buttonProps={{
                          placement: 'outside',
                          label: 'Add phone',
                          icon: 'plus',
                          className: classNames(styles.fieldRepeaterButton, {
                            [styles.hideRepeaterButton]: isSCIMEnabled,
                          }),
                        }}
                        className={styles.fieldRepeater}
                        style={{ borderTop: 'none' }}
                        component={FieldRepeater}
                        rowProps={{
                          type: 'tel',
                          selectedIndex: primary_phone_idx,
                          onSelect: setPrimaryPhoneNumberIndex,
                          disabled: fieldRules.phone_numbers.disabled,
                        }}
                        withBackground={false}
                        rowComponent={ContactPointField}
                        name="phone_numbers"
                      />
                    </FieldWrapper>
                  </Tooltip>
                </FormField>
              )}

              {fieldRules.user_type.visible && (
                <FormField>
                  <FieldWrapper
                    label="User category"
                    helpIcon={{
                      text: userTypeTooltipContent,
                    }}
                  >
                    <UserTypeField />
                  </FieldWrapper>
                </FormField>
              )}

              {fieldRules.custom_roles.visible && (
                <FormField>
                  <RolesField
                    required={fieldRules.custom_roles.required}
                    disabled={fieldRules.custom_roles.disabled}
                    onInit={(roles) =>
                      patchInitialValues('custom_roles', roles)
                    }
                  />
                </FormField>
              )}

              {fieldRules.company_id.visible && (
                <FormField>
                  <Field
                    label="Company"
                    name="company_id"
                    placeholder="Select a company or create a new one"
                    component={FieldSelect}
                    items={companies}
                    searchable
                    suggestionComponent={TagSelectSuggestion}
                    onSuggest={addCompany}
                    searchSuggestion={(items: Company[], search: string) =>
                      items.find(
                        (item) =>
                          item.name.toLowerCase() === search.toLowerCase(),
                      )
                    }
                    renderSuggestion={(search: string) => `Add "${search}"`}
                    disabled={fieldRules.company_id.disabled}
                    required={fieldRules.company_id.required}
                  />
                </FormField>
              )}

              {fieldRules.pricing_plan.visible && (
                <FormField>
                  <FlexBox
                    as="label"
                    gap={sizes.spacer[250]}
                    alignItems="center"
                  >
                    <Checkbox
                      name="pricingPlan"
                      checked={isPricingPlanAccepted}
                      onChange={() =>
                        setIsPricingPlanAccepted((isAccepted) => !isAccepted)
                      }
                    />

                    <Text.Paragraph>
                      You will be charged with each
                      <Text.Paragraph as="strong" variant="1">
                        {' '}
                        new agent{' '}
                      </Text.Paragraph>
                      based on your contract. If you have any questions, just
                      reach out to your account manager for additional details.{' '}
                      <Text.ExternalLink
                        href={`https://${config.domain.web}/pricing`}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        See pricing <Icon name="external-link" />
                      </Text.ExternalLink>
                    </Text.Paragraph>
                  </FlexBox>
                </FormField>
              )}
            </FormGrid>
          </ModalBody>

          <ModalFoot
            cancelOnClick={closeModal}
            cancelName="Cancel"
            confirmOnClick={noop} // A hack to show the confirm button 🤷
            confirmName={userId === null ? 'Add new user' : 'Save'}
            confirmProps={{
              isLoading: submitting || asyncValidating !== false,
              disabled:
                fieldRules.pricing_plan.required ||
                submitting ||
                pristine ||
                asyncValidating !== false ||
                meta.asyncStatus === 'loading',
              id: 'userSaveChanges',
              type: 'submit',
            }}
          />
        </form>
      </div>
    </Modal>
  );
}

export const UserModal = reduxForm({
  form: userModalFormName,
  validate,
  asyncValidate,
  asyncChangeFields: ['emails[].value', 'phone_numbers[].value'],
  // redux-form issue - https://github.com/redux-form/redux-form/issues/3944#issuecomment-1236699349
  shouldAsyncValidate: (params) =>
    defaultShouldAsyncValidate({
      ...params,
      syncValidationPasses: true,
    }),
})(UserModalImpl);
